import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../layouts/AuthLayout';
import { AppLayout } from '../layouts/AppLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { NotFoundPage } from '../pages';

// Lazy-loaded feature pages.
const LoginPage = lazy(() =>
  import('@features/auth').then((m) => ({ default: m.LoginPage })),
);
const ConversationsPage = lazy(() =>
  import('@features/conversations').then((m) => ({ default: m.ConversationsPage })),
);
const CancellationsPage = lazy(() =>
  import('@features/cancellations').then((m) => ({ default: m.CancellationsPage })),
);
const TermsPage = lazy(() => import('@features/legal').then((m) => ({ default: m.TermsPage })));

function SuspenseFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center min-h-screen text-sm text-[var(--color-text-secondary)]">
      {t('common.loading')}
    </div>
  );
}

const withSuspense = (node: ReactNode): ReactNode => (
  <Suspense fallback={<SuspenseFallback />}>{node}</Suspense>
);

/**
 * Application router configuration.
 *
 * The product surfaces are the general inbox and the recapture-cancellation cohort.
 * No signup and no password reset (both are planned, see the docs).
 *
 * The inbox is one page serving two paths — the second only adds the open
 * conversation's uuid, which the page turns into the chat drawer. That is what
 * makes an open conversation a real, shareable URL and lets Back close it
 * (ADR-0015). A third segment names the drawer's panel (ADR-0026). `/` redirects there: it is the only surface there is, so landing
 * on an empty home would be a dead step.
 */
export const router = createBrowserRouter(
  [
    // Auth routes (public).
    {
      element: <AuthLayout />,
      children: [{ path: '/login', element: withSuspense(<LoginPage />) }],
    },

    // Legal documents (public, no session needed — a marketplace listing links here).
    { path: '/terms', element: withSuspense(<TermsPage />) },

    // Protected routes — the app shell.
    {
      element: <ProtectedRoute />,
      children: [
        {
          element: <AppLayout />,
          children: [
            { path: '/', element: <Navigate to="/conversations" replace /> },
            { path: '/conversations', element: withSuspense(<ConversationsPage />) },
            {
              path: '/conversations/:conversationUuid',
              element: withSuspense(<ConversationsPage />),
            },
            // The drawer's panel (`case` | `timeline`) is a path segment, so each
            // tab is a deep link (ADR-0026). No segment = the conversation.
            {
              path: '/conversations/:conversationUuid/:panel',
              element: withSuspense(<ConversationsPage />),
            },
            { path: '/cancellations', element: withSuspense(<CancellationsPage />) },
            {
              path: '/cancellations/:conversationUuid',
              element: withSuspense(<CancellationsPage />),
            },
            {
              path: '/cancellations/:conversationUuid/:panel',
              element: withSuspense(<CancellationsPage />),
            },
          ],
        },
      ],
    },

    // 404.
    { path: '*', element: <NotFoundPage /> },
  ],
  {
    future: { v7_relativeSplatPath: true },
  },
);
